import React, {useEffect, useState} from 'react';
import {SpaceBetween, Container, Header} from '@amzn/awsui-components-react';
import {Notification} from '../navigation/page-layout';
import {ChatContainer} from './chat-container';
import {ChatInput} from './chat-input';
import {Message, ChatSession} from './types';
import {useChatbotApi} from '../hooks/use-chatbot-api';
import '../../assets/styles/chatbot-styles.scss';

/**
 * Props for the ChatbotPage component
 */
export interface ChatbotPageProps {
    /** Function to push notifications to the page layout */
    pushNotification: (notification: Notification) => void;
}

/**
 * Session storage key for persisting chat sessions
 */
const SESSION_STORAGE_KEY = 'f3-portal-chatbot-session';

/**
 * Generates a unique ID for messages and sessions
 */
function generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Main ChatbotPage component that provides the AI chatbot interface
 *
 * Requirements:
 * - 1.1: Display dedicated chatbot page with conversation interface
 * - 1.4: Preserve chat session across navigation during browser session
 * - 2.4: Disable UI during API calls
 * - 4.2: Clear chat history when starting new conversation
 * - 4.3: Reset chat session state on new conversation
 * - 4.4: Discard previous conversation history on new conversation
 * - 5.1: Display error messages in chat interface
 * - 5.2: Re-enable input after errors for retry
 */
export function ChatbotPage(props: ChatbotPageProps) {
    // Chat state management
    const [messages, setMessages] = useState<Message[]>([]);
    const [sessionId, setSessionId] = useState<string>(generateId());
    const [isTyping, setIsTyping] = useState(false);
    const [hasShownWelcome, setHasShownWelcome] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);

    // API hook for chatbot interactions
    const {sendMessage, isLoading, error} = useChatbotApi();

    /**
     * Load chat session from sessionStorage on mount
     * Requirement 1.4: Preserve chat session across navigation
     */
    useEffect(() => {
        const savedSession = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (savedSession) {
            try {
                const session: ChatSession = JSON.parse(savedSession);
                // Convert timestamp strings back to Date objects
                const messagesWithDates = session.messages.map((msg) => ({
                    ...msg,
                    timestamp: new Date(msg.timestamp),
                }));
                setMessages(messagesWithDates);
                setSessionId(session.id);
                setHasShownWelcome(true); // Don't show welcome if we have existing messages
            } catch (err) {
                console.error('Failed to load chat session from storage:', err);
                // If loading fails, start fresh
                sessionStorage.removeItem(SESSION_STORAGE_KEY);
            }
        }
    }, []);

    /**
     * Show welcome message with typing animation on first load
     */
    useEffect(() => {
        if (!hasShownWelcome && messages.length === 0) {
            // Wait 1 second before showing typing indicator
            const typingTimeout = setTimeout(() => {
                setIsTyping(true);

                // Show typing for 2 seconds, then display welcome message
                const messageTimeout = setTimeout(() => {
                    const welcomeMessage: Message = {
                        id: generateId(),
                        content:
                            "Hello! I'm Excelsior AI, your intelligent assistant for the F3 Portal. I can help you with:\n\n• Understanding forecast trends and data\n• Navigating operational metrics and dashboards\n• Answering questions about portal features\n• Providing insights on your data\n\nWhat would you like to know?",
                        role: 'assistant',
                        timestamp: new Date(),
                    };
                    setMessages([welcomeMessage]);
                    setIsTyping(false);
                    setHasShownWelcome(true);
                }, 2000);

                return () => clearTimeout(messageTimeout);
            }, 1000);

            return () => clearTimeout(typingTimeout);
        }
    }, [hasShownWelcome, messages.length]);

    /**
     * Save chat session to sessionStorage whenever messages change
     * Requirement 1.4: Persist session across navigation
     */
    useEffect(() => {
        if (messages.length > 0) {
            const session: ChatSession = {
                id: sessionId,
                messages,
                createdAt: new Date(),
                updatedAt: new Date(),
            };
            sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
        }
    }, [messages, sessionId]);

    /**
     * Display error notifications when API errors occur
     * Requirement 5.1: Display error messages
     */
    useEffect(() => {
        if (error) {
            props.pushNotification({
                type: 'error',
                header: 'Chatbot Error',
                content: error.message,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    /**
     * Handles sending a message to the chatbot
     * Requirements:
     * - 2.4: Disable UI during API call
     * - 5.2: Re-enable input after error
     */
    const handleSendMessage = async (messageText: string) => {
        // Create user message
        const userMessage: Message = {
            id: generateId(),
            content: messageText,
            role: 'user',
            timestamp: new Date(),
        };

        // Add user message to chat history immediately
        setMessages((prevMessages) => [...prevMessages, userMessage]);

        // Track if we've added the assistant message yet
        const assistantMessageId = generateId();
        let messageAdded = false;

        try {
            // Send message to API with streaming callback
            await sendMessage(messageText, sessionId, undefined, (streamedContent: string) => {
                // On first chunk, add the assistant message and hide loading indicator
                if (!messageAdded) {
                    messageAdded = true;
                    setIsStreaming(true);
                    const assistantMessage: Message = {
                        id: assistantMessageId,
                        content: streamedContent,
                        role: 'assistant',
                        timestamp: new Date(),
                    };
                    setMessages((prevMessages) => [...prevMessages, assistantMessage]);
                } else {
                    // Update the assistant message with streamed content
                    setMessages((prevMessages) =>
                        prevMessages.map((msg) => (msg.id === assistantMessageId ? {...msg, content: streamedContent} : msg))
                    );
                }
            });
        } catch (err) {
            // Remove the assistant message on error if it was added
            if (messageAdded) {
                setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== assistantMessageId));
            }
            // Error is handled by useEffect above
            // UI is automatically re-enabled when isLoading becomes false
            console.error('Failed to send message:', err);
        } finally {
            // Reset streaming state
            setIsStreaming(false);
        }
    };

    /**
     * Handles starting a new conversation
     * Requirements:
     * - 4.1: Clear current chat history
     * - 4.2: Display welcome message again
     * - 4.3: Reset chat session state
     * - 4.4: Discard previous conversation history
     */
    const handleNewConversation = () => {
        // Clear messages
        setMessages([]);

        // Generate new session ID
        setSessionId(generateId());

        // Clear session storage
        sessionStorage.removeItem(SESSION_STORAGE_KEY);

        // Reset welcome message flag to show it again
        setHasShownWelcome(false);

        // Show confirmation notification
        props.pushNotification({
            type: 'success',
            content: 'Started new conversation',
        });
    };

    return (
        <div className="chatbot-page" data-testid="chatbot-page">
            <SpaceBetween size="l" direction="vertical">
                <Header
                    variant="h1"
                    description="Ask questions about forecasts, operational data, and portal features. Get instant insights powered by AI."
                >
                    Excelsior AI
                </Header>
                <Container disableContentPaddings>
                    <div className="chat-wrapper">
                        {/* Chat container displays messages */}
                        <ChatContainer
                            messages={messages}
                            isLoading={(isLoading || isTyping) && !isStreaming}
                            onPromptClick={handleSendMessage}
                        />

                        {/* Chat input for user to send messages */}
                        {/* Requirement 2.4: Disable UI during API calls */}
                        <ChatInput
                            onSendMessage={handleSendMessage}
                            onNewConversation={handleNewConversation}
                            disabled={isLoading}
                        />
                    </div>
                </Container>
            </SpaceBetween>
        </div>
    );
}
